import * as prismic from '@prismicio/client'
import { enableAutoPreviews } from '@prismicio/next'
import { setFullLocale } from './utils/utils'

/**
 * Redis client
 * @type {Redis | undefined}
 */
let redis
if (
  typeof window === 'undefined' &&
  process.env.REDIS_URL &&
  process.env.PRISMIC_CACHE_KEY
) {
  import('ioredis').then((Redis) => {
    redis = new Redis.default(process.env.REDIS_URL)
  })
}

const endpoint = process.env.PRISMIC_URL
const REPOSITORY_REF_TTL = 60 * 15 // 15 minutes (same as document cache)

let hasLoggedMissingTokenInProd = false

const checkPrismicTokenInProd = () => {
  if (
    typeof window === 'undefined' &&
    process.env.ENV === 'prod' &&
    !process.env.PRISMIC_ACCESS_TOKEN &&
    !hasLoggedMissingTokenInProd
  ) {
    hasLoggedMissingTokenInProd = true
    console.error(
      '[Prismic] PRISMIC_ACCESS_TOKEN is missing in production. All API calls will be made without token, which may count against your Prismic quota.'
    )
  }
}

const logPrismicCall = (context, hasToken) => {
  if (process.env.PRISMIC_DEBUG_LOGS === 'true') {
    const tokenStatus = hasToken
      ? 'WITH access_token ✓'
      : 'WITHOUT access_token ✗'
    console.log(`[Prismic] ${context} - ${tokenStatus}`)
  }
}

/**
 * Wrapper fetch pour tracer les requêtes Prismic et FORCER le token sur l'endpoint repository (/api/v2).
 * Le client Prismic peut appeler /api/v2 sans token (getCachedRepository) ; on l'ajoute systématiquement.
 * Activé via PRISMIC_DEBUG_LOGS=true pour les logs.
 */
const createPrismicFetch = () => {
  const realFetch =
    typeof globalThis.fetch === 'function' ? globalThis.fetch : undefined
  if (!realFetch) return undefined

  return (url, options = {}) => {
    let finalUrl = url
    const isRepositoryEndpoint =
      typeof url === 'string' &&
      url.includes('prismic') &&
      url.includes('/api/v2') &&
      !url.includes('/api/v2/documents/')
    if (isRepositoryEndpoint) {
      const token = process.env.PRISMIC_ACCESS_TOKEN
      if (token && !url.includes('access_token=')) {
        finalUrl = `${
          url.includes('?') ? url + '&' : url + '?'
        }access_token=${token}`
      }
    }
    const headers = new Headers(options.headers)
    headers.set('User-Agent', 'lizee-debug/1.0')

    try {
      const parsedUrl = new URL(finalUrl)
      const queryParams = Object.fromEntries(parsedUrl.searchParams.entries())
      console.log('[Prismic fetch]', {
        url: `${parsedUrl.origin}${parsedUrl.pathname}`,
        headers: Object.fromEntries(headers.entries()),
        queryParams,
      })
    } catch {
      console.log('[Prismic fetch]', { url: finalUrl })
    }

    return realFetch(finalUrl, { ...options, headers })
  }
}
const REPOSITORY_CACHE_KEY = process.env.PRISMIC_CACHE_KEY
  ? `prismic:${process.env.PRISMIC_CACHE_KEY}:repository-ref`
  : null

/**
 * Fetch vers l'endpoint repository (/api/v2) - on force toujours le token si dispo.
 * Le client Prismic peut faire des appels /api/v2 sans token dans certains cas ;
 * ce wrapper garantit que toutes les requêtes repository ont le token.
 */
const fetchRepositoryWithToken = (url) => {
  const token = process.env.PRISMIC_ACCESS_TOKEN
  const urlWithToken =
    token && !url.includes('access_token=')
      ? `${url.includes('?') ? url + '&' : url + '?'}access_token=${token}` // eslint-disable-line
      : url
  const reqHeaders = { 'User-Agent': 'lizee-debug/1.0' }

  try {
    const parsedUrl = new URL(urlWithToken)
    const queryParams = Object.fromEntries(parsedUrl.searchParams.entries())
    console.log('[Prismic fetchRepository]', {
      url: `${parsedUrl.origin}${parsedUrl.pathname}`,
      headers: reqHeaders,
      queryParams,
    })
  } catch {
    console.log('[Prismic fetchRepository]', { url: urlWithToken })
  }

  return fetch(urlWithToken, { headers: reqHeaders })
}

/**
 * Get the Prismic master ref from Redis cache or by fetching the Repository API.
 * Caching the ref in Redis avoids /api/v2 calls on every serverless cold start.
 */
const getMasterRef = async () => {
  if (typeof window !== 'undefined') return undefined

  if (redis && REPOSITORY_CACHE_KEY) {
    const cached = await redis.get(REPOSITORY_CACHE_KEY)
    if (cached) return cached
  }

  const hasToken = !!process.env.PRISMIC_ACCESS_TOKEN
  logPrismicCall('getMasterRef (repository /api/v2)', hasToken)

  const response = await fetchRepositoryWithToken(endpoint)
  if (!response.ok) {
    throw new Error(`Prismic repository fetch failed: ${response.status}`)
  }
  const data = await response.json()
  const masterRef = data.refs?.find((r) => r.isMasterRef)?.ref
  if (!masterRef) {
    throw new Error('No master ref in Prismic repository')
  }

  if (redis && REPOSITORY_CACHE_KEY) {
    await redis.set(REPOSITORY_CACHE_KEY, masterRef, 'EX', REPOSITORY_REF_TTL)
  }

  return masterRef
}

/**
 * Shared Prismic client singleton (server-side only).
 * Uses ref from Redis cache to avoid /api/v2 repository calls on cold starts.
 */
let sharedClient

const getSharedClient = () => {
  if (!sharedClient) {
    const accessToken = process.env.PRISMIC_ACCESS_TOKEN
    logPrismicCall('createClient (shared) - client configuré', !!accessToken)
    const clientOptions = {
      ref: getMasterRef,
      accessToken,
    }
    const prismicFetch = createPrismicFetch()
    if (prismicFetch) {
      clientOptions.fetch = prismicFetch
    }
    sharedClient = prismic.createClient(endpoint, clientOptions)
  }
  return sharedClient
}

/**
 * Create a Prismic client
 * @description Returns the shared singleton client for normal requests,
 * or a fresh client when preview mode is active (previewData / req).
 * Uses Redis-cached master ref to avoid /api/v2 calls on every serverless instance.
 * @warning Prismic api calls should be only on server side
 * @param {Object} config - The configuration object
 */
export const createClient = (config = {}) => {
  checkPrismicTokenInProd()
  const isPreview = config.previewData || config.req
  const accessToken = process.env.PRISMIC_ACCESS_TOKEN

  if (isPreview) {
    logPrismicCall('createClient (preview) - client configuré', !!accessToken)
  }

  const clientOptions = {
    ref: getMasterRef,
    accessToken,
  }
  const prismicFetch = createPrismicFetch()
  if (prismicFetch) {
    clientOptions.fetch = prismicFetch
  }
  const client = isPreview
    ? prismic.createClient(endpoint, clientOptions)
    : getSharedClient()

  enableAutoPreviews({
    client,
    previewData: config.previewData,
    req: config.req,
  })

  return client
}

/**
 * Wrapper function for Prismic API calls with Redis caching and retry logic
 * @description Handles caching, retry logic, and error handling for Prismic API calls
 * @param {string} cacheKey - The Redis cache key
 * @param {Function} fetchFn - The Prismic API call function to execute
 * @param {string} errorContext - Context string for error logging
 */
const NOT_FOUND = '__NOT_FOUND__'
const CACHE_TTL = 60 * 15 // 15 minutes
const CACHE_TTL_ALL = 60 * 60 // 1 hour for getAllDocuments (used by sitemap, changes rarely)
const NOT_FOUND_TTL = 60 * 60 * 24 // 24 hours
const RETRY_TTL = 60 // 1 minute
const MAX_RETRIES = 3

// --- Lizee CMS ---
const USE_LIZEE_CMS = process.env.USE_LIZEE_CMS === 'true'

const lizetCMSFetch = async (path, queryParams = {}) => {
  const baseUrl = process.env.RMS_API_URL
  const token = process.env.RMS_PUBLIC_API_TOKEN
  const url = new URL(`${baseUrl}${path}`)

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  })

  const response = await fetch(url.toString(), {
    headers: { 'x-api-key': token },
  })

  if (response.status === 404) return null
  if (!response.ok)
    throw new Error(`Lizee CMS fetch failed: ${response.status}`)

  return response.json()
}

const getLizeeSingleDocument = (lang, type) => {
  const cacheKey = process.env.PRISMIC_CACHE_KEY
    ? `lizee-cms:${process.env.PRISMIC_CACHE_KEY}:${type}:${lang}`
    : null

  return withPrismicCache(
    cacheKey,
    async () => {
      const data = await lizetCMSFetch('/api/cms/documents', {
        type,
        lang,
        pageSize: 1,
      })
      return data?.results?.[0] ?? null
    },
    `Error fetching Lizee CMS document "${type}" in "${lang}":`
  )
}

const getLizeeByUID = (documentType, uid, lang) => {
  const cacheKey = process.env.PRISMIC_CACHE_KEY
    ? `lizee-cms:${process.env.PRISMIC_CACHE_KEY}:${documentType}:${uid}:${lang}`
    : null

  return withPrismicCache(
    cacheKey,
    async () => {
      const data = await lizetCMSFetch('/api/cms/documents', {
        type: documentType,
        lang,
      })
      return data?.results?.find((doc) => doc.uid === uid) ?? null
    },
    `Error fetching Lizee CMS document "${documentType}" with UID "${uid}" in "${lang}":`
  )
}

const getLizeeByType = (documentType, lang) => {
  const cacheKey = process.env.PRISMIC_CACHE_KEY
    ? `lizee-cms:${process.env.PRISMIC_CACHE_KEY}:type:${documentType}:${lang}`
    : null

  return withPrismicCache(
    cacheKey,
    () => lizetCMSFetch('/api/cms/documents', { type: documentType, lang }),
    `Error fetching Lizee CMS documents of type "${documentType}" in "${lang}":`
  )
}

const getLizeeAllDocuments = () => {
  const cacheKey = process.env.PRISMIC_CACHE_KEY
    ? `lizee-cms:${process.env.PRISMIC_CACHE_KEY}:all`
    : null

  return withPrismicCache(
    cacheKey,
    () => lizetCMSFetch('/api/cms/documents'),
    'Error fetching all Lizee CMS documents:',
    CACHE_TTL_ALL
  )
}

const isDocumentNotFound = (error) =>
  error?.message?.includes('No documents were returned')

const logPrismicCacheError = (message, { cacheKey, error, ...extra } = {}) => {
  console.error(`[Prismic cache] ${message}`, {
    cacheKey: cacheKey ?? null,
    ...(error && {
      errorMessage: error?.message,
      errorStack: error?.stack,
    }),
    ...extra,
  })
}

const logPrismicCacheDebug = (message, { cacheKey, ...extra } = {}) => {
  if (process.env.PRISMIC_DEBUG_LOGS !== 'true') return

  console.log(`[Prismic cache] ${message}`, {
    cacheKey: cacheKey ?? null,
    ...extra,
  })
}

const withPrismicCache = async (
  cacheKey,
  fetchFn,
  errorContext,
  ttl = CACHE_TTL
) => {
  try {
    if (typeof window !== 'undefined') {
      return await fetchFn()
    }

    if (!redis || !cacheKey) {
      logPrismicCacheDebug('Cache bypassed (Redis or cacheKey missing)', {
        cacheKey,
        hasRedis: Boolean(redis),
        errorContext,
      })
      return await fetchFn()
    }

    const cachedData = await redis.get(cacheKey)
    if (cachedData) {
      const parsed = JSON.parse(cachedData)

      if (parsed === NOT_FOUND) {
        logPrismicCacheDebug('Cache hit: NOT_FOUND', { cacheKey })
        return null
      }

      if (typeof parsed === 'object' && parsed !== null) {
        logPrismicCacheDebug('Cache hit', { cacheKey })
        return parsed
      }

      logPrismicCacheDebug('Cache miss (retry counter)', {
        cacheKey,
        retryCount: parsed,
      })
      // If it's a number (retry count), proceed to retry
    } else {
      logPrismicCacheDebug('Cache miss', { cacheKey, errorContext })
    }

    const data = await fetchFn()

    if (data) {
      await redis.set(cacheKey, JSON.stringify(data), 'EX', ttl)
      logPrismicCacheDebug('Fetched and cached', { cacheKey, ttl })
    } else {
      logPrismicCacheDebug('Fetch returned empty data', {
        cacheKey,
        errorContext,
      })
    }
    return data
  } catch (error) {
    logPrismicCacheError(errorContext, { cacheKey, error })

    if (redis && cacheKey) {
      // If the general document fails, purge the entire client cache
      if (cacheKey.includes('general')) {
        const prefix = `prismic:${process.env.PRISMIC_CACHE_KEY}:*`
        try {
          const keys = await redis.keys(prefix)
          if (keys.length > 0) {
            await redis.del(...keys)
          }
          logPrismicCacheError(
            'Purged client cache after general document failure',
            { cacheKey, keysDeleted: keys.length, prefix }
          )
        } catch (purgeError) {
          logPrismicCacheError('Failed to purge client cache', {
            cacheKey,
            error: purgeError,
            prefix,
          })
        }
      }

      // Document doesn't exist in this Prismic repo — cache for 24h, no retry
      if (isDocumentNotFound(error)) {
        logPrismicCacheError('Document not found, caching NOT_FOUND marker', {
          cacheKey,
          error,
          notFoundTtl: NOT_FOUND_TTL,
        })
        await redis.set(
          cacheKey,
          JSON.stringify(NOT_FOUND),
          'EX',
          NOT_FOUND_TTL
        )
        return null
      }

      const cachedData = await redis.get(cacheKey)
      const parsed = cachedData ? JSON.parse(cachedData) : null

      // Fall back to stale cached document if available
      if (typeof parsed === 'object' && parsed !== null) {
        logPrismicCacheError('Returning stale cached data after fetch error', {
          cacheKey,
          error,
        })
        await redis.expire(cacheKey, ttl)
        return parsed
      }

      let retryCount = 0
      if (typeof parsed === 'number') {
        retryCount = parsed
      }

      if (retryCount < MAX_RETRIES) {
        logPrismicCacheError('Fetch failed, incrementing retry counter', {
          cacheKey,
          error,
          retryCount: retryCount + 1,
          maxRetries: MAX_RETRIES,
          retryTtl: RETRY_TTL,
        })
        await redis.set(
          cacheKey,
          JSON.stringify(retryCount + 1),
          'EX',
          RETRY_TTL
        )
      } else {
        logPrismicCacheError('Max retries reached, caching null response', {
          cacheKey,
          error,
          maxRetries: MAX_RETRIES,
          cacheTtl: CACHE_TTL,
        })
        await redis.set(cacheKey, JSON.stringify(null), 'EX', CACHE_TTL)
      }
    } else {
      logPrismicCacheError(
        'Cannot recover via Redis (missing client or cacheKey)',
        {
          cacheKey,
          error,
          hasRedis: Boolean(redis),
        }
      )
    }
    return null
  }
}

/**
 * Get a single document from Prismic
 * @description Get a single document from Prismic with the given configuration
 * @warning Prismic api calls should be only on server side
 * @param {Object} client - The Prismic client
 * @param {string} lang - The language of the document
 * @param {string} document - The document type
 */
export const getSingleDocument = async (client, lang, document) => {
  if (USE_LIZEE_CMS) return getLizeeSingleDocument(lang, document)

  logPrismicCall(
    `getSingleDocument(${document}, ${lang})`,
    !!process.env.PRISMIC_ACCESS_TOKEN
  )

  const cacheKey = process.env.PRISMIC_CACHE_KEY
    ? `prismic:${process.env.PRISMIC_CACHE_KEY}:${document}:${lang}`
    : null

  return withPrismicCache(
    cacheKey,
    () => client.getSingle(document, { lang }),
    `Error fetching document "${document}" in "${lang}":`
  )
}

/**
 * Get a document by UID from Prismic
 * @description Get a document by UID from Prismic with the given configuration
 * @warning Prismic api calls should be only on server side
 * @param {Object} client - The Prismic client
 * @param {string} documentType - The document type
 * @param {string} uid - The document UID
 * @param {string} lang - The language of the document
 */
export const getByUID = async (client, documentType, uid, lang) => {
  if (USE_LIZEE_CMS) return getLizeeByUID(documentType, uid, lang)

  logPrismicCall(
    `getByUID(${documentType}, ${uid}, ${lang})`,
    !!process.env.PRISMIC_ACCESS_TOKEN
  )

  const cacheKey = process.env.PRISMIC_CACHE_KEY
    ? `prismic:${process.env.PRISMIC_CACHE_KEY}:${documentType}:${uid}:${lang}`
    : null

  return withPrismicCache(
    cacheKey,
    () => client.getByUID(documentType, uid, { lang }),
    `Error fetching document "${documentType}" with UID "${uid}" in "${lang}":`
  )
}

/**
 * Get documents by type from Prismic
 * @warning Prismic api calls should be only on server side
 * @param {Object} client - The Prismic client
 * @param {string} documentType - The document type to query
 * @param {string} lang - The language of the documents
 */
export const getByType = async (client, documentType, lang) => {
  if (USE_LIZEE_CMS) return getLizeeByType(documentType, lang)

  logPrismicCall(
    `getByType(${documentType}, ${lang})`,
    !!process.env.PRISMIC_ACCESS_TOKEN
  )

  const cacheKey = process.env.PRISMIC_CACHE_KEY
    ? `prismic:${process.env.PRISMIC_CACHE_KEY}:type:${documentType}:${lang}`
    : null

  return withPrismicCache(
    cacheKey,
    () =>
      client.get({
        predicates: prismic.predicate.at('document.type', documentType),
        lang,
      }),
    `Error fetching documents of type "${documentType}" in "${lang}":`
  )
}

/**
 * Get all documents from Prismic (no type filter)
 * @warning Prismic api calls should be only on server side
 * @param {Object} client - The Prismic client
 */
export const getAllDocuments = async (client) => {
  if (USE_LIZEE_CMS) return getLizeeAllDocuments()

  logPrismicCall('getAllDocuments()', !!process.env.PRISMIC_ACCESS_TOKEN)

  const cacheKey = process.env.PRISMIC_CACHE_KEY
    ? `prismic:${process.env.PRISMIC_CACHE_KEY}:all`
    : null

  return withPrismicCache(
    cacheKey,
    () => client.get({ predicates: '' }),
    'Error fetching all documents:',
    CACHE_TTL_ALL
  )
}

/**
 * Get multiple documents from Prismic
 * @description Get multiple documents from Prismic with the given configuration
 * @warning Prismic api calls should be only on server side
 * @param {Object} documents - The documents to get
 * @param {string} locale - The language of the documents
 * @param {Object} previewData - The preview data
 */
export const getPrismicDocuments = async (documents, locale, previewData) => {
  const client = createClient({ previewData })
  const lang = setFullLocale(locale)

  const data = await Promise.all(
    Object.values(documents).map((document) =>
      getSingleDocument(client, lang, document)
    )
  )

  return Object.fromEntries(
    Object.keys(documents).map((key, index) => [key, data[index]])
  )
}
